import { useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api from "@/api";
import { buildInvoicePdfFilename } from "@/utils/helpers";
import { computeInvoiceTotals } from "@/lib/invoiceCalc";
import {
  buildBalanceDeductionRows,
  getPracticeBilling,
  resolvePracticeFeeMode,
} from "@/lib/practiceBilling";

/**
 * Custom hook for managing Invoices and Billing operations
 */
export function useInvoices({ invoices, setInvoices, cases, setCases, clients, settings }) {
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null); // null = create mode, id = edit mode
  const [saveError, setSaveError] = useState(null); // Error message for save operation
  const [isPreviewLoading, setIsPreviewLoading] = useState(false); // Preview loading state
  
  // PDF Preview Modal state
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewPdfBlob, setPreviewPdfBlob] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  // Office-spec filename composed by the consumers (auto-open after
  // create + manual preview) and forwarded to <PDFPreviewModal>. Mirrors
  // backend `build_invoice_pdf_filename`.
  const [previewFilename, setPreviewFilename] = useState("");

  // Office-configurable defaults for NEW invoices (admin-editable in
  // the Settings UI, resolved from the canonical studio_settings doc).
  // Each value has a safe runtime fallback so the form always renders
  // a valid preselection even before settings load. `editInvoice`
  // keeps its own legacy fallbacks to avoid silently migrating
  // historical records.
  const defaultPaymentMethod = settings?.defaultInvoicePaymentMethod || "Bonifico";
  const defaultVatRate = (() => {
    const v = settings?.defaultInvoiceVatRate;
    return v === undefined || v === null || v === "" || Number.isNaN(Number(v))
      ? 22
      : Number(v);
  })();
  const defaultManagementType = settings?.defaultInvoiceManagementType === "studio"
    ? "studio"
    : "agenzia";
  // Booleans: explicit `false` honoured; anything else → true (safe
  // office default, consistent with backend backfill).
  const defaultContributoEnabled = settings?.defaultInvoiceContributoEnabled !== false;
  const defaultImportoLordo = settings?.defaultInvoiceImportoLordo !== false;
  
  const [invoiceForm, setInvoiceForm] = useState({
    invoiceNumber: "",
    invoiceDate: new Date().toISOString().split("T")[0],
    issueDate: new Date().toISOString().split("T")[0],
    clientId: "",
    caseId: "",
    managementType: "agenzia",
    imponibile: "",
    iva: 22,
    ivaAmount: 0,
    totale: 0,
    contributoIntegrativo: 0, // 4% contribution
    contributoEnabled: true, // Auto-calculate by default
    speseArt15: 0, // Art. 15 expenses
    shippingExpensesAmount: 0, // 11/06/2026 — Spese di Spedizione (non-VAT)
    importoLordo: true, // DEFAULT: Importo lordo (IVA inclusa)
    notes: "",
    // Initial seed for the form before any "Nuova Fattura" click.
    // Once the user opens the dialog via "Nuova Fattura" (or "Fattura
    // da Pratica") the form is re-seeded by resetInvoiceForm /
    // createInvoiceFromCase, which read the live, admin-configurable
    // `settings.defaultInvoicePaymentMethod`. We keep a hardcoded
    // "Bonifico" here only as a safe boot-time value before settings
    // have loaded.
    paymentMethod: "Bonifico",
    status: "Bozza",
    markAsPaid: false, // If true, immediately mark as paid and add payment
    billingStage: null,
    documentType: "invoice", // 28/02/2026: "invoice" | "proforma"
  });

  const resetInvoiceForm = useCallback((documentType = "invoice") => {
    setInvoiceForm({
      invoiceNumber: "",
      invoiceDate: new Date().toISOString().split("T")[0],
      issueDate: new Date().toISOString().split("T")[0],
      clientId: "",
      caseId: "",
      managementType: defaultManagementType,
      imponibile: "",
      iva: defaultVatRate,
      ivaAmount: 0,
      totale: 0,
      contributoIntegrativo: 0,
      contributoEnabled: defaultContributoEnabled,
      speseArt15: 0,
      shippingExpensesAmount: 0,
      importoLordo: defaultImportoLordo,
      notes: "",
      paymentMethod: defaultPaymentMethod,
      status: "Bozza",
      markAsPaid: false,
      billingStage: null,
      documentType,
    });
    setEditingInvoiceId(null);
    setSaveError(null);
    setIsInvoiceDialogOpen(false);
  }, [
    defaultPaymentMethod,
    defaultVatRate,
    defaultManagementType,
    defaultContributoEnabled,
    defaultImportoLordo,
  ]);

  /**
   * Open the InvoiceDialog in CREATE mode for a Documento Proforma
   * (28/02/2026). Shares the same dialog, form and validation as the
   * official invoice flow; the only distinction is the documentType
   * discriminator that drives numbering, listings and PDF title.
   */
  const openNewProforma = useCallback(() => {
    resetInvoiceForm("proforma");
    setIsInvoiceDialogOpen(true);
  }, [resetInvoiceForm]);

  // Open invoice for editing
  const editInvoice = useCallback((invoice) => {
    if (!invoice) return;
    
    // speseArt15 in DB is the ORIGINAL user value (without bollo) for new invoices.
    // For legacy invoices (no bolloAutomatico field), speseArt15 might include bollo.
    const hasSeparateBollo = invoice.bolloAutomatico !== undefined && invoice.bolloAutomatico !== null && invoice.bolloAutomatico > 0;
    let originalSpeseArt15 = Number(invoice.speseArt15) || 0;
    
    if (!hasSeparateBollo && originalSpeseArt15 > 77.47) {
      // Legacy invoice: speseArt15 likely includes €2 bollo, subtract it
      originalSpeseArt15 = originalSpeseArt15 - 2;
    }
    // For new invoices (hasSeparateBollo true), speseArt15 is already the original value
    
    setInvoiceForm({
      invoiceNumber: invoice.invoiceNumber || "",
      invoiceDate: invoice.invoiceDate?.split("T")[0] || new Date().toISOString().split("T")[0],
      issueDate: invoice.issueDate?.split("T")[0] || new Date().toISOString().split("T")[0],
      clientId: invoice.clientId || "",
      caseId: invoice.caseId || "",
      managementType: invoice.managementType || "studio",
      imponibile: invoice.imponibile ? String(invoice.imponibile) : "",
      iva: invoice.iva || 22,
      ivaAmount: invoice.ivaAmount || 0,
      totale: invoice.totale || 0,
      contributoIntegrativo: invoice.contributoIntegrativo || 0,
      speseArt15: originalSpeseArt15,
      bolloAutomatico: hasSeparateBollo ? Number(invoice.bolloAutomatico) : 0,
      // 11/06/2026 — Spese di Spedizione
      shippingExpensesAmount: Number(invoice.shippingExpensesAmount) || 0,
      importoLordo: false, // Editing: imponibile in DB is already NET, must NOT re-scorporate
      notes: invoice.notes || "",
      paymentMethod: invoice.paymentMethod || "RimessaDiretta",
      status: invoice.status || "Bozza",
      markAsPaid: false,
      manualRows: invoice.manualRows || [],
      linkedPractices: invoice.linkedPractices || [],
      billingStage: invoice.billingStage || null,
      // Preserva il discriminatore del documento (28/02/2026):
      // editare una proforma deve continuare a salvarla come proforma.
      documentType: invoice.documentType || "invoice",
    });
    setEditingInvoiceId(invoice.id);
    setIsInvoiceDialogOpen(true);
  }, []);

  // Duplica fattura (11/06/2026) — apre il dialog editor con i campi
  // business clonati dalla fattura sorgente ma SENZA:
  //   • invoiceNumber (verrà assegnato al salvataggio dal counter)
  //   • invoiceDate / issueDate (di default oggi)
  //   • payments / paymentStatus / status (resta in Bozza)
  //   • creditNoteIds / sourceInvoiceId / convertedToInvoiceId (i
  //     riferimenti hanno senso solo sull'originale)
  // È una variante di `editInvoice` con `editingInvoiceId=null` così il
  // salvataggio crea un NUOVO documento invece di aggiornare l'esistente.
  const duplicateInvoice = useCallback((invoice) => {
    if (!invoice) return;

    const hasSeparateBollo = invoice.bolloAutomatico !== undefined
      && invoice.bolloAutomatico !== null
      && invoice.bolloAutomatico > 0;
    let originalSpeseArt15 = Number(invoice.speseArt15) || 0;
    if (!hasSeparateBollo && originalSpeseArt15 > 77.47) {
      originalSpeseArt15 = originalSpeseArt15 - 2;
    }

    const today = new Date().toISOString().split("T")[0];
    setInvoiceForm({
      // Identità: numero vuoto → il backend assegnerà il next-number
      // ufficiale solo al SAVE. La data è oggi (data di duplicazione).
      invoiceNumber: "",
      invoiceDate: today,
      issueDate: today,
      // Riferimenti business clonati 1:1
      clientId: invoice.clientId || "",
      caseId: invoice.caseId || "",
      managementType: invoice.managementType || "studio",
      // Importi e configurazione fiscale
      imponibile: invoice.imponibile ? String(invoice.imponibile) : "",
      iva: invoice.iva || 22,
      ivaAmount: invoice.ivaAmount || 0,
      totale: invoice.totale || 0,
      contributoIntegrativo: invoice.contributoIntegrativo || 0,
      speseArt15: originalSpeseArt15,
      bolloAutomatico: hasSeparateBollo ? Number(invoice.bolloAutomatico) : 0,
      // 11/06/2026 — Spese di Spedizione clonate sulla nuova bozza
      shippingExpensesAmount: Number(invoice.shippingExpensesAmount) || 0,
      importoLordo: false,  // importi in DB sono già netti
      // Split Payment ereditato dalla sorgente (cliente PA resta PA)
      splitPaymentEnabled: !!invoice.splitPaymentEnabled,
      // Contenuto duplicato
      notes: invoice.notes || "",
      paymentMethod: invoice.paymentMethod || "RimessaDiretta",
      manualRows: invoice.manualRows ? JSON.parse(JSON.stringify(invoice.manualRows)) : [],
      linkedPractices: invoice.linkedPractices
        ? JSON.parse(JSON.stringify(invoice.linkedPractices))
        : [],
      // Stato resettato: la duplicata nasce in Bozza, mai pagata
      status: "Bozza",
      markAsPaid: false,
      // Tipo documento: una duplicata di fattura resta fattura; una
      // duplicata di proforma resta proforma. Mai duplicare una NdC.
      documentType: invoice.documentType === "proforma" ? "proforma" : "invoice",
      // NB: NON cloniamo payments, creditNoteIds, sourceInvoiceId,
      // sourceProformaId, convertedToInvoiceId — la duplicata è un
      // documento NUOVO e indipendente.
    });
    // editingInvoiceId=null → POST /api/invoices (nuovo doc) al save
    setEditingInvoiceId(null);
    setIsInvoiceDialogOpen(true);
  }, []);

  // Create invoice OR proforma pre-filled from a case/practice.
  // 27/02/2026 — Aggiunto secondo parametro `documentType` per supportare
  // la creazione diretta di Documento Proforma dalla lista "Da Fatturare"
  // (richiesta ufficio). Riusa interamente il motore fattura: cambia solo
  // il discriminatore che pilota numerazione, listing, PDF e dialog title.
  const createInvoiceFromCase = useCallback((caseItem, documentType = "invoice") => {
    if (!caseItem) return;

    // Determine management type from practice type
    const managementType = caseItem.practiceType === "Gestionale Studio" ? "studio" : "agenzia";

    const billing = caseItem._billing || getPracticeBilling(caseItem, invoices, clients);
    const feeMode = resolvePracticeFeeMode(caseItem, invoices, clients);
    const isBalance = ["Conclusa", "Chiusa", "Completata"].includes(caseItem.status);
    const balanceRows = isBalance ? buildBalanceDeductionRows(billing) : [];

    // Build rich description from practice data
    const descParts = [];
    if (caseItem.caseNumber) descParts.push(`Pratica ${caseItem.caseNumber}`);
    if (caseItem.subject) descParts.push(caseItem.subject);
    if (caseItem.practiceArea) descParts.push(caseItem.practiceArea);
    if (caseItem.enteCoinvolto) descParts.push(`Ente: ${caseItem.enteCoinvolto}`);
    const description = descParts.join(" - ");

    // Pre-fill invoice form with all available data
    setInvoiceForm({
      invoiceNumber: "",
      invoiceDate: new Date().toISOString().split("T")[0],
      issueDate: new Date().toISOString().split("T")[0],
      clientId: caseItem.clientId || "",
      caseId: caseItem.id || "",
      managementType: managementType,
      // SALDO: parte dall'intero compenso pattuito e porta gli acconti
      // precedenti come righe negative. ACCONTO: campo libero, con il
      // residuo usato solo come capienza massima.
      imponibile: isBalance && billing.agreedAmount > 0 ? String(billing.agreedAmount) : "",
      iva: defaultVatRate,
      notes: isBalance ? `Compenso complessivo pattuito - ${description}` : description,
      // Default ufficio configurabile da Impostazioni Studio (vedi
      // resetInvoiceForm). Anche il flusso "Fattura da Pratica" rispetta
      // la stessa preferenza condivisa.
      paymentMethod: defaultPaymentMethod,
      contributoEnabled: defaultContributoEnabled,
      importoLordo: feeMode === "lordo",
      speseArt15: 0,
      shippingExpensesAmount: 0,
      manualRows: balanceRows,
      linkedPractices: [caseItem.id],
      billingStage: isBalance ? "saldo" : "acconto",
      practiceAgreedAmount: billing.agreedAmount,
      practiceResidualAmount: billing.residualAmount,
      practiceFeeMode: feeMode,
      // 27/02/2026 — Discriminatore documento: "invoice" (default) | "proforma".
      // Il backend usa lo stesso engine; cambia solo numerazione, PDF e listing.
      documentType: documentType === "proforma" ? "proforma" : "invoice",
    });

    // Open the invoice dialog
    setIsInvoiceDialogOpen(true);
  }, [clients, invoices, defaultPaymentMethod, defaultVatRate, defaultContributoEnabled]);

  const handleSaveInvoice = useCallback(async (overrideData = null) => {
    // Prefer the override payload passed by the dialog's `handleSave`
    // (which has the freshly-computed totals + manualRows). Falls back
    // to `invoiceForm` for any legacy caller that invokes onSave() with
    // no arguments.
    const formData = overrideData || invoiceForm;

    // Clear previous errors
    setSaveError(null);

    if (!formData.clientId) {
      setSaveError("Cliente obbligatorio");
      return;
    }
    // Accept invoices that have either a main imponibile OR at least one
    // manual row with a non-zero amount (the InvoiceDialog now lets the
    // operator build invoices entirely out of free-text rows).
    const hasMainAmount = Number(formData.imponibile) > 0;
    const hasManualRows = Array.isArray(formData.manualRows)
      && formData.manualRows.some(r => Number(r?.amount || r?.amountInput || 0) > 0);
    if (!hasMainAmount && !hasManualRows) {
      setSaveError("Inserisci un imponibile o almeno una riga manuale con importo");
      return;
    }

    // The InvoiceDialog has just run `flushSync(setInvoiceForm(saveData))`
    // right before calling onSave(), so `invoiceForm` already carries the
    // totals computed by `/lib/invoiceCalc.js`:
    //   - imponibile  → scorporated NET (in LORDO mode) or NET (in NET mode)
    //   - manualRows  → each row holds `amount` (net) AND `amountInput` (gross)
    //   - contributoIntegrativo / ivaAmount / arrotondamento / totale → final
    //   - importoLordo → boolean flag preserved for re-edit
    // We DO NOT recompute here: doing so would re-scorporate an already
    // scorporated imponibile, producing wrong totals.

    const imponibile = Number(formData.imponibile) || 0;
    const ivaPercentage = Number(formData.iva) || 0;
    const ivaAmount = Number(formData.ivaAmount) || 0;
    const contributoIntegrativo = Number(formData.contributoIntegrativo) || 0;
    const arrotondamento = Number(formData.arrotondamento) || 0;
    const totale = Number(formData.totale) || 0;

    // speseArt15 in form = ORIGINAL value (no bollo). Backend stores both.
    const speseArt15Input = Number(formData.speseArt15) || 0;
    const bolloAutomatico = Number(formData.bolloAutomatico) || 0;

    const manualRows = formData.manualRows || [];

    // Determine status
    let finalStatus = "Emessa";
    if (formData.markAsPaid && (formData.paymentMethod === "Contanti" || formData.paymentMethod === "POS" || formData.paymentMethod === "Carta" || formData.paymentMethod === "ContantiVistaFattura")) {
      finalStatus = "Pagata";
    }

    const invoiceData = {
      invoiceNumber: editingInvoiceId ? formData.invoiceNumber : (formData.invoiceNumber || ""),  // Use previewed number if available
      invoiceDate: formData.invoiceDate,
      issueDate: formData.issueDate || new Date().toISOString(),
      clientId: formData.clientId,
      caseId: formData.caseId || null,
      managementType: formData.managementType,
      imponibile,
      contributoIntegrativo,
      speseArt15: speseArt15Input,     // Save ORIGINAL value WITHOUT bollo
      bolloAutomatico,                  // Save bollo separately
      iva: ivaPercentage,
      ivaAmount,
      arrotondamento,
      totale,
      status: finalStatus,
      paymentMethod: formData.paymentMethod,
      notes: formData.notes,
      manualRows: manualRows,
      linkedPractices: formData.linkedPractices || [],
      importoLordo: formData.importoLordo === true,
      billingStage: formData.billingStage || null,
      paymentDate: finalStatus === "Pagata"
        ? (formData.paymentDate || formData.invoiceDate || new Date().toISOString().split("T")[0])
        : null,
      // 28/02/2026 — passa il discriminatore del documento al backend:
      // "invoice" (default) → fattura fiscale ufficiale; "proforma" →
      // Documento Proforma (numerazione e disclaimer separati).
      documentType: formData.documentType || "invoice",
      // 11/06/2026 — Bug fix: erano omessi dal cherry-pick. Senza questi
      // campi il backend riceveva 0/false e il PDF non poteva stampare
      // né la riga "Spese di Spedizione" né il blocco Split Payment.
      shippingExpensesAmount: (() => {
        const raw = String(formData.shippingExpensesAmount ?? "").replace(",", ".");
        const n = parseFloat(raw);
        return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0;
      })(),
      splitPaymentEnabled: !!formData.splitPaymentEnabled,
    };

    try {
      let savedInvoice;
      const isProforma = (invoiceData.documentType === "proforma");

      if (editingInvoiceId) {
        // UPDATE existing invoice
        savedInvoice = await api.updateInvoice(editingInvoiceId, invoiceData);
        // 27/02/2026 — Se è una proforma, non resta nell'array `invoices`
        // (che è server-filtered a documentType=invoice). Rimuoviamola e
        // notifichiamo il listing dedicato.
        if (isProforma) {
          setInvoices(prev => prev.filter(inv => inv.id !== editingInvoiceId));
        } else {
          setInvoices(prev => prev.map(inv => inv.id === editingInvoiceId ? savedInvoice : inv));
        }
      } else {
        // CREATE new invoice
        savedInvoice = await api.createInvoice(invoiceData);
        // Aggiungiamo solo le fatture ufficiali allo state `invoices`;
        // le proforme sono gestite da uno state separato in FatturazioneTab.
        if (!isProforma) {
          setInvoices(prev => [savedInvoice, ...prev]);
        }

        // Il backend aggiorna `invoiceId` soltanto quando questa fattura
        // completa davvero il compenso pattuito. Non forziamo il flag dal
        // frontend: un acconto (o una proforma) deve lasciare la pratica
        // disponibile nella coda "Da fatturare".
      }

      // If marked as paid immediately, add a payment record
      if (formData.markAsPaid && finalStatus === "Pagata") {
        try {
          // 27/02/2026 — Usa la data effettiva del pagamento indicata
          // dall'operatore (può essere antecedente alla data fattura).
          // Fallback alla data fattura, poi a oggi.
          const effectivePaymentDate =
            formData.paymentDate ||
            formData.invoiceDate ||
            new Date().toISOString().split("T")[0];
          await api.addInvoicePayment(savedInvoice.id, {
            amount: totale,
            paymentDate: effectivePaymentDate,
            paymentMethod: formData.paymentMethod === "Carta" ? "POS" : formData.paymentMethod === "Contanti" ? "Cassa" : formData.paymentMethod,
            notes: "Pagamento immediato"
          });
          // Refresh the invoice to get updated payment data
          const refreshed = await api.getInvoices();
          setInvoices(refreshed);
        } catch (paymentError) {
          console.error("Error adding payment:", paymentError);
        }
      }

      resetInvoiceForm();

      // Refresh cases to pick up audit notes written by the backend
      try {
        const refreshedCases = await api.getCases();
        setCases(refreshedCases);
      } catch (e) {
        console.error("Error refreshing cases after invoice save:", e);
      }

      // ───────── Auto-open generated PDF after CREATE (acceptance criteria) ─────────
      // Only on CREATE (not UPDATE) to avoid bothering the operator with a
      // popup on every minor edit. The invoice is already persisted in the
      // DB at this point; any PDF generation failure is non-fatal (the
      // operator can re-open from the invoice row at any time).
      if (!editingInvoiceId && savedInvoice?.id) {
        toast.success(
          isProforma ? "Documento Proforma creato correttamente" : "Fattura creata correttamente",
          {
            description: `N. ${savedInvoice.invoiceNumber || savedInvoice.id}`,
          }
        );
        // 27/02/2026 — Notifica ai listing di Proforma (FatturazioneTab)
        // che esiste un nuovo documento, così possono ricaricarsi senza
        // essere accoppiati al hook.
        if (isProforma) {
          try { window.dispatchEvent(new CustomEvent("gi:proforma-saved", { detail: { id: savedInvoice.id } })); } catch {}
        }
        try {
          const blob = await api.getInvoicePDFBlob(savedInvoice.id);
          // Surface the PDF using the same in-app preview modal already used
          // by "Anteprima PDF" — no popup blocker, no extra tab, identical
          // print/download controls.
          const clientObj = clients.find(c => c.id === savedInvoice.clientId);
          setPreviewFilename(buildInvoicePdfFilename({
            invoiceNumber: savedInvoice.invoiceNumber,
            invoiceDate: savedInvoice.invoiceDate,
            clientName: clientObj?.fullName || "",
          }));
          setPreviewPdfBlob(blob);
          setIsPreviewModalOpen(true);
        } catch (pdfErr) {
          console.error("[invoice-save] PDF auto-open failed:", pdfErr);
          toast.error("Fattura salvata ma PDF non generato", {
            description:
              pdfErr?.message ||
              "Apri la fattura dalla lista per scaricare/stampare il PDF.",
            duration: 6000,
          });
        }
      } else if (editingInvoiceId) {
        toast.success(
          isProforma ? "Documento Proforma aggiornato correttamente" : "Fattura aggiornata correttamente"
        );
        if (isProforma) {
          try { window.dispatchEvent(new CustomEvent("gi:proforma-saved", { detail: { id: editingInvoiceId } })); } catch {}
        }
      }
    } catch (error) {
      setSaveError(error.message || "Errore durante il salvataggio");
    }
  }, [invoiceForm, editingInvoiceId, cases, setCases, setInvoices, resetInvoiceForm]);

  // Preview invoice PDF without saving - returns blob for in-app modal
  const handlePreviewInvoice = useCallback(async (previewFormData = null) => {
    // Use passed data or fall back to invoiceForm
    const formData = previewFormData || invoiceForm;

    // Accept previews where the operator built the invoice entirely from
    // free-text manual rows (no main imponibile required).
    if (!formData.clientId) {
      setSaveError("Cliente obbligatorio per l'anteprima");
      return;
    }
    const hasMainAmount = Number(formData.imponibile) > 0;
    const hasManualRows = Array.isArray(formData.manualRows)
      && formData.manualRows.some(r => Number(r?.amount || r?.amountInput || 0) > 0);
    if (!hasMainAmount && !hasManualRows) {
      setSaveError("Inserisci un imponibile o almeno una riga manuale per generare l'anteprima");
      return;
    }

    setIsPreviewLoading(true);
    setPreviewError(null);
    setPreviewPdfBlob(null);
    // Compose office-spec filename now that we know the form's client +
    // number + date. Falls back gracefully when any are missing —
    // `buildInvoicePdfFilename` handles defaults.
    const clientObj = clients.find(c => c.id === formData.clientId);
    setPreviewFilename(buildInvoicePdfFilename({
      invoiceNumber: formData.invoiceNumber || "ANTEPRIMA",
      invoiceDate: formData.invoiceDate || new Date().toISOString().slice(0, 10),
      clientName: clientObj?.fullName || "",
    }));
    setIsPreviewModalOpen(true); // Open modal immediately to show loading state
    
    try {
      let previewData;
      
      // If previewFormData was passed, it already has calculated values
      if (previewFormData && previewFormData.ivaAmount !== undefined) {
        previewData = {
          invoiceNumber: previewFormData.invoiceNumber || "ANTEPRIMA",
          invoiceDate: previewFormData.invoiceDate,
          clientId: previewFormData.clientId,
          caseId: previewFormData.caseId || null,
          managementType: previewFormData.managementType,
          imponibile: previewFormData.imponibile,
          contributoIntegrativo: previewFormData.contributoIntegrativo,
          speseArt15: previewFormData.speseArt15,
          bolloAutomatico: previewFormData.bolloAutomatico,
          arrotondamento: previewFormData.arrotondamento || 0,
          iva: previewFormData.iva,
          ivaAmount: previewFormData.ivaAmount,
          totale: previewFormData.totale,
          paymentMethod: previewFormData.paymentMethod,
          notes: previewFormData.notes,
          manualRows: previewFormData.manualRows || [],
          importoLordo: previewFormData.importoLordo === true,
          markAsPaid: previewFormData.markAsPaid || false,
        };
      } else {
        // Fallback path — no precomputed totals were passed (e.g. a
        // future caller that triggers a preview directly with the raw
        // form state). Route through the SAME calculation engine used
        // by InvoiceDialog so Art.15 / bollo €2 / LORDO scorporo stay
        // consistent (regression test G/H/I in invoiceCalc.test.mjs).
        const totals = computeInvoiceTotals({
          imponibileInput: formData.imponibile,
          manualRows: formData.manualRows || [],
          importoLordo: formData.importoLordo || false,
          ivaPercentage: formData.iva,
          contributoEnabled: formData.contributoEnabled !== false,
          contributoIntegrativoManual: formData.contributoIntegrativo,
          speseArt15Input: formData.speseArt15,
        });

        previewData = {
          invoiceNumber: formData.invoiceNumber || "ANTEPRIMA",
          invoiceDate: formData.invoiceDate,
          clientId: formData.clientId,
          caseId: formData.caseId || null,
          managementType: formData.managementType,
          imponibile: totals.imponibilePrincipaleNet,
          contributoIntegrativo: totals.contributoIntegrativo,
          speseArt15: totals.speseArt15,           // ORIGINAL (no bollo)
          bolloAutomatico: totals.bolloAutomatico, // separated
          arrotondamento: totals.arrotondamento,
          iva: totals.ivaPercentage,
          ivaAmount: totals.ivaAmount,
          totale: totals.totale,
          paymentMethod: formData.paymentMethod,
          notes: formData.notes,
          manualRows: totals.manualRowsBreakdown,
          importoLordo: !!formData.importoLordo,
        };
      }

      const result = await api.previewInvoicePDF(previewData);
      
      if (result.success && result.blob) {
        setPreviewPdfBlob(result.blob);
      } else {
        throw new Error("Impossibile generare l'anteprima PDF");
      }
    } catch (error) {
      console.error("Preview error:", error);
      setPreviewError(error.message || "Errore durante la generazione dell'anteprima");
    } finally {
      setIsPreviewLoading(false);
    }
  }, [invoiceForm]);

  // Close preview modal.
  //
  // React 19 + Radix Dialog + PDF.js hardening (post-validation fix):
  // Earlier we deferred the auxiliary resets with `setTimeout(300)` to
  // wait out the Radix close animation, but on real Windows browsers
  // the timer could fire on either side of the actual portal teardown
  // and the crash reappeared after a few open/close cycles.
  //
  // The modal now early-returns `null` when `isOpen` flips to false
  // (see PDFPreviewModal.jsx), so React unmounts the whole Dialog
  // subtree synchronously. That makes any deferred state reset
  // pointless AND introduces a new risk: if the user reopens the
  // preview within 300ms of closing the old one, a still-pending
  // timeout would null out the freshly-set blob.
  //
  // Reset everything synchronously here. No timers, no race.
  const closePreviewModal = useCallback(() => {
    setIsPreviewModalOpen(false);
    setPreviewPdfBlob(null);
    setPreviewError(null);
    setPreviewFilename("");
    setIsPreviewLoading(false);
  }, []);

  const updateInvoiceStatus = useCallback(async (invoiceId, newStatus) => {
    const invoice = invoices.find(inv => inv.id === invoiceId);
    if (!invoice) return;

    try {
      const updated = await api.updateInvoice(invoiceId, {
        ...invoice,
        status: newStatus,
        paymentDate: newStatus === "Pagata" ? new Date().toISOString().split("T")[0] : null
      });
      setInvoices(prev => prev.map(inv => inv.id === invoiceId ? updated : inv));
    } catch (error) {
      console.error("Error updating invoice status:", error);
    }
  }, [invoices, setInvoices]);

  const exportInvoiceXML = useCallback((invoice) => {
    const client = clients.find(c => c.id === invoice.clientId);
    const caseItem = invoice.caseId ? cases.find(c => c.id === invoice.caseId) : null;

    const xmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<p:FatturaElettronica xmlns:ds="http://www.w3.org/2000/09/xmldsig#" 
  xmlns:p="http://ivaservizi.agenziaentrate.gov.it/docs/xsd/fatture/v1.2" 
  versione="FPR12">
  <FatturaElettronicaHeader>
    <DatiTrasmissione>
      <IdTrasmittente>
        <IdPaese>IT</IdPaese>
        <IdCodice>STUDIO_BONAIUTI</IdCodice>
      </IdTrasmittente>
      <ProgressivoInvio>${invoice.invoiceNumber}</ProgressivoInvio>
      <FormatoTrasmissione>FPR12</FormatoTrasmissione>
    </DatiTrasmissione>
    <CessionarioCommittente>
      <DatiAnagrafici>
        <CodiceFiscale>${client?.taxCode || ""}</CodiceFiscale>
        <Anagrafica>
          <Denominazione>${client?.fullName || ""}</Denominazione>
        </Anagrafica>
      </DatiAnagrafici>
    </CessionarioCommittente>
  </FatturaElettronicaHeader>
  <FatturaElettronicaBody>
    <DatiGenerali>
      <DatiGeneraliDocumento>
        <TipoDocumento>TD01</TipoDocumento>
        <Divisa>EUR</Divisa>
        <Data>${invoice.invoiceDate}</Data>
        <Numero>${invoice.invoiceNumber}</Numero>
        <ImportoTotaleDocumento>${invoice.totale?.toFixed(2)}</ImportoTotaleDocumento>
        ${caseItem ? `<Causale>Pratica ${caseItem.caseNumber} - ${caseItem.subject}</Causale>` : ""}
      </DatiGeneraliDocumento>
    </DatiGenerali>
    <DatiBeniServizi>
      <DettaglioLinee>
        <NumeroLinea>1</NumeroLinea>
        <Descrizione>${invoice.notes || "Prestazione professionale"}</Descrizione>
        <PrezzoUnitario>${invoice.imponibile?.toFixed(2)}</PrezzoUnitario>
        <PrezzoTotale>${invoice.imponibile?.toFixed(2)}</PrezzoTotale>
        <AliquotaIVA>${invoice.iva?.toFixed(2)}</AliquotaIVA>
      </DettaglioLinee>
      <DatiRiepilogo>
        <AliquotaIVA>${invoice.iva?.toFixed(2)}</AliquotaIVA>
        <ImponibileImporto>${invoice.imponibile?.toFixed(2)}</ImponibileImporto>
        <Imposta>${invoice.ivaAmount?.toFixed(2)}</Imposta>
      </DatiRiepilogo>
    </DatiBeniServizi>
    <DatiPagamento>
      <CondizioniPagamento>TP02</CondizioniPagamento>
      <DettaglioPagamento>
        <ModalitaPagamento>MP05</ModalitaPagamento>
        <ImportoPagamento>${invoice.totale?.toFixed(2)}</ImportoPagamento>
      </DettaglioPagamento>
    </DatiPagamento>
  </FatturaElettronicaBody>
</p:FatturaElettronica>`;

    const blob = new Blob([xmlContent], { type: "application/xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${invoice.invoiceNumber.replace(/\//g, "_")}.xml`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }, [clients, cases]);

  // Add payment to invoice
  const addInvoicePayment = useCallback(async (invoiceId, payment) => {
    try {
      const updated = await api.addInvoicePayment(invoiceId, payment);
      setInvoices(prev => prev.map(inv => inv.id === invoiceId ? updated : inv));
      // Refresh cases to pick up audit notes
      try {
        const refreshedCases = await api.getCases();
        setCases(refreshedCases);
      } catch (e) { console.error("Error refreshing cases:", e); }
      return updated;
    } catch (error) {
      console.error("Error adding payment:", error);
      throw error;
    }
  }, [setInvoices, setCases]);

  // Remove payment from invoice
  const removeInvoicePayment = useCallback(async (invoiceId, paymentIndex) => {
    try {
      const updated = await api.removeInvoicePayment(invoiceId, paymentIndex);
      setInvoices(prev => prev.map(inv => inv.id === invoiceId ? updated : inv));
      return updated;
    } catch (error) {
      console.error("Error removing payment:", error);
      throw error;
    }
  }, [setInvoices]);

  // Refresh invoices from server
  const refreshInvoices = useCallback(async () => {
    try {
      const freshInvoices = await api.getInvoices();
      setInvoices(freshInvoices);
    } catch (error) {
      console.error("Error refreshing invoices:", error);
    }
  }, [setInvoices]);

  // Delete invoice
  const deleteInvoice = useCallback(async (invoiceId) => {
    try {
      await api.deleteInvoice(invoiceId);
      setInvoices(prev => prev.filter(inv => inv.id !== invoiceId));
    } catch (error) {
      console.error("Error deleting invoice:", error);
      throw error;
    }
  }, [setInvoices]);

  // Clear save error callback
  const clearSaveError = useCallback(() => {
    setSaveError(null);
  }, []);

  return {
    // Dialog state
    isInvoiceDialogOpen,
    setIsInvoiceDialogOpen,
    invoiceForm,
    setInvoiceForm,
    editingInvoiceId,
    setEditingInvoiceId,
    saveError,
    clearSaveError,
    isPreviewLoading,
    
    // PDF Preview Modal state
    isPreviewModalOpen,
    previewPdfBlob,
    previewError,
    previewFilename,
    closePreviewModal,

    // Actions
    handleSaveInvoice,
    handlePreviewInvoice,
    updateInvoiceStatus,
    createInvoiceFromCase,
    editInvoice,
    duplicateInvoice,
    exportInvoiceXML,
    resetInvoiceForm,
    openNewProforma,
    
    // Payment management
    addInvoicePayment,
    removeInvoicePayment,
    refreshInvoices,
    deleteInvoice
  };
}

/**
 * Custom hook for managing Deadlines operations
 */
export function useDeadlines({ deadlines, setDeadlines, activeCases }) {
  const [isDeadlineDialogOpen, setIsDeadlineDialogOpen] = useState(false);
  const [editingDeadline, setEditingDeadline] = useState(null);
  const [deadlineForm, setDeadlineForm] = useState({
    title: "",
    caseId: "",
    dueDate: "",
    description: ""
  });

  const resetDeadlineForm = useCallback(() => {
    setDeadlineForm({
      title: "",
      caseId: "",
      dueDate: "",
      description: ""
    });
    setEditingDeadline(null);
    setIsDeadlineDialogOpen(false);
  }, []);

  const handleSaveDeadline = useCallback(async () => {
    if (!deadlineForm.title || !deadlineForm.caseId || !deadlineForm.dueDate) {
      alert("Titolo, pratica e data scadenza sono obbligatori");
      return;
    }

    try {
      if (editingDeadline) {
        const updated = await api.updateDeadline(editingDeadline.id, {
          ...editingDeadline,
          ...deadlineForm
        });
        setDeadlines(prev => prev.map(d => d.id === editingDeadline.id ? updated : d));
      } else {
        const created = await api.createDeadline({
          ...deadlineForm,
          status: "in_sospeso",
          type: "manuale"
        });
        setDeadlines(prev => [created, ...prev]);
      }
      resetDeadlineForm();
    } catch (error) {
      alert(error.message || "Errore durante il salvataggio");
    }
  }, [deadlineForm, editingDeadline, setDeadlines, resetDeadlineForm]);

  const toggleDeadlineStatus = useCallback(async (deadlineId) => {
    const deadline = deadlines.find(d => d.id === deadlineId);
    if (!deadline) return;

    const newStatus = deadline.status === "completata" ? "in_sospeso" : "completata";

    try {
      const updated = await api.updateDeadline(deadlineId, {
        ...deadline,
        status: newStatus,
        completedDate: newStatus === "completata" ? new Date().toISOString() : null
      });
      setDeadlines(prev => prev.map(d => d.id === deadlineId ? updated : d));
    } catch (error) {
      console.error("Error toggling deadline status:", error);
    }
  }, [deadlines, setDeadlines]);

  const handleDeleteDeadline = useCallback(async (deadlineId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa scadenza?")) {
      return;
    }

    try {
      await api.deleteDeadline(deadlineId);
      setDeadlines(prev => prev.filter(d => d.id !== deadlineId));
    } catch (error) {
      alert(error.message || "Errore durante l'eliminazione");
    }
  }, [setDeadlines]);

  return {
    // Dialog state
    isDeadlineDialogOpen,
    setIsDeadlineDialogOpen,
    editingDeadline,
    deadlineForm,
    setDeadlineForm,

    // Actions
    handleSaveDeadline,
    toggleDeadlineStatus,
    handleDeleteDeadline,
    resetDeadlineForm
  };
}
